import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootStateOrAny } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TextField, CircularProgress } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import axios from 'axios';
import { constructSearchObj } from '../../utils/utils';
import AppConstants from '../../AppConstants';
import { getEntity as getRestaurant } from '../../redux/actions/restaurantsActions';

interface Restaurant {
  id: number;
  number: number;
}

interface RestaurantSearchParams {
  limit: number;
  brand: string;
  page: number;
  number?: string;
}

interface RestaurantAPIResponse {
  restaurants: Restaurant[];
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
}

interface RestaurantSelectorProps {
  disabled?: boolean;
  className?: string;
}

const RestaurantSelector: React.FC<RestaurantSelectorProps> = ({
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Local state for dropdown functionality
  const [open, setOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMoreRestaurants, setHasMoreRestaurants] = useState<boolean>(true);
  const [allRestaurants, setAllRestaurants] = useState<Restaurant[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [internalSelectedRestaurant, setInternalSelectedRestaurant] =
    useState<Restaurant | null>(null);
  const [searchPending, setSearchPending] = useState<boolean>(false);
  const initializedRef = useRef<boolean>(false);
  const userHasSelectedRef = useRef<boolean>(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Redux selectors (only what we need)
  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const permissions = useSelector(
    (state: RootStateOrAny) => state.UserReducer.permissions,
  );
  const userInfo = useSelector(
    (state: RootStateOrAny) => state.UserReducer.userInfo,
  );

  // Direct API call for restaurants
  const fetchRestaurantsAPI = useCallback(
    async (params: RestaurantSearchParams): Promise<RestaurantAPIResponse> => {
      const endpoint = `${process.env.REACT_APP_ENDPOINT_RESTAURANTS}${constructSearchObj(params)}`;
      const response = await axios.get<RestaurantAPIResponse>(endpoint);
      return response.data;
    },
    [],
  );

  // Debounced search function
  const performSearch = useCallback(
    async (searchTerm: string, page = 1, append = false) => {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const searchParams: RestaurantSearchParams = {
          limit: AppConstants.rowsPerPage,
          brand: selectedBrand,
          page,
        };

        // Add search parameter if search term exists
        if (searchTerm.trim()) {
          // Remove # if user typed it and ensure we have a valid number
          const cleanSearchTerm = searchTerm.replace(/^#/, '').trim();
          if (cleanSearchTerm) {
            searchParams.number = cleanSearchTerm;
          }
        }

        const result = await fetchRestaurantsAPI(searchParams);
        const newRestaurants = result?.restaurants || [];
        const totalRestaurants = result?.pagination?.total || 0;

        if (append) {
          setAllRestaurants(prev => {
            const updatedList = [...prev, ...newRestaurants];
            setHasMoreRestaurants(updatedList.length < totalRestaurants);
            return updatedList;
          });
        } else {
          setAllRestaurants(newRestaurants);
          setHasMoreRestaurants(newRestaurants.length < totalRestaurants);
          setCurrentPage(1);
        }
      } catch (error) {
        // Handle error silently
        if (!append) {
          setAllRestaurants([]);
          setHasMoreRestaurants(false);
        }
      } finally {
        if (!append) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [selectedBrand, fetchRestaurantsAPI],
  );

  // Load more restaurants function
  const loadMoreRestaurants = useCallback(async () => {
    if (loadingMore || !hasMoreRestaurants) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await performSearch(searchValue, nextPage, true);
  }, [
    loadingMore,
    hasMoreRestaurants,
    currentPage,
    searchValue,
    performSearch,
  ]);

  // Initialize restaurants on component mount
  useEffect(() => {
    if (
      permissions.length &&
      selectedBrand &&
      userInfo &&
      Object.keys(userInfo).length &&
      !initializedRef.current
    ) {
      initializedRef.current = true;
      userHasSelectedRef.current = false;
      setInternalSelectedRestaurant(null);
      setAllRestaurants([]);
      setCurrentPage(1);
      setHasMoreRestaurants(true);

      const loadInitialRestaurants = async () => {
        try {
          const searchParams: RestaurantSearchParams = {
            limit: AppConstants.rowsPerPage,
            brand: selectedBrand,
            page: 1,
          };

          const result = await fetchRestaurantsAPI(searchParams);
          const restaurants = result?.restaurants || [];
          const totalRestaurants = result?.pagination?.total || 0;

          setAllRestaurants(restaurants);
          setHasMoreRestaurants(restaurants.length < totalRestaurants);
          setCurrentPage(1);

          // Auto-select first restaurant if available and user hasn't selected one
          if (restaurants.length > 0 && !userHasSelectedRef.current) {
            const firstRestaurant = restaurants[0];
            setInternalSelectedRestaurant(firstRestaurant);
            dispatch(getRestaurant(firstRestaurant.id));
          }
        } catch (error) {
          // Handle error silently
          setAllRestaurants([]);
          setHasMoreRestaurants(false);
        } finally {
          setLoading(false);
        }
      };

      loadInitialRestaurants();
    }
  }, [selectedBrand, permissions, userInfo, fetchRestaurantsAPI, dispatch]);

  // Reset initialization when brand changes
  useEffect(() => {
    initializedRef.current = false;
  }, [selectedBrand]);

  // Debounced search effect
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Don't search if user has selected a restaurant and input matches
    if (
      internalSelectedRestaurant &&
      inputValue === `#${internalSelectedRestaurant.number}`
    ) {
      setSearchPending(false);
      return;
    }

    // Set search pending when user is typing and we have input
    if (open && inputValue.trim() && inputValue !== searchValue) {
      setSearchPending(true);
    } else {
      setSearchPending(false);
    }

    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      if (open && inputValue !== searchValue) {
        setSearchPending(false);
        setSearchValue(inputValue);
        performSearch(inputValue, 1, false);
      }
    }, 1000);

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      setSearchPending(false);
    };
  }, [
    inputValue,
    open,
    internalSelectedRestaurant,
    searchValue,
    performSearch,
  ]);

  // Update input value when internal selected restaurant changes
  useEffect(() => {
    if (internalSelectedRestaurant) {
      setInputValue(`#${internalSelectedRestaurant.number}`);
    } else {
      setInputValue('');
    }
  }, [internalSelectedRestaurant]);

  const handleRestaurantChange = (
    _event: unknown,
    newValue: Restaurant | null,
  ) => {
    // Mark that user has made a manual selection
    userHasSelectedRef.current = true;
    setInternalSelectedRestaurant(newValue);

    // Dispatch to Redux to fetch and set the restaurant
    if (newValue) {
      dispatch(getRestaurant(newValue.id));
    }
  };

  const formatRestaurantDisplay = (restaurant: Restaurant): string => {
    return `#${restaurant.number}`;
  };

  return (
    <Autocomplete
      id="restaurant-autocomplete"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={internalSelectedRestaurant}
      onChange={handleRestaurantChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        // Only update input value if user is typing (not selecting)
        if (reason === 'input') {
          setInputValue(newInputValue);
        }
      }}
      getOptionSelected={(option, value) => option.id === value.id}
      getOptionLabel={option => `#${option.number}`}
      options={allRestaurants || []}
      loading={loading}
      disabled={loading || disabled}
      size="small"
      className={className}
      ListboxProps={{
        onScroll: (event: React.SyntheticEvent) => {
          const listboxNode = event.currentTarget;
          if (
            listboxNode.scrollTop + listboxNode.clientHeight >=
              listboxNode.scrollHeight - 10 &&
            !loadingMore &&
            hasMoreRestaurants
          ) {
            loadMoreRestaurants();
          }
        },
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={t('restaurant.list.gridLabels.restaurantNumber')}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || loadingMore) && (
                  <CircularProgress color="inherit" size={20} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={option => <div>{formatRestaurantDisplay(option)}</div>}
      noOptionsText={
        searchPending ? '' : t('restaurant.list.gridLabels.noMatchCriteria')
      }
    />
  );
};

export default RestaurantSelector;
